import * as Yup from 'yup';

import { STRIPE, STRIPE_APPLE } from 'lib/constants';

const isNotStripe = val => val !== STRIPE && val !== STRIPE_APPLE;

// Common validation messages
const requiredField = 'This field is required.';
const phoneNumberErrorMessage = '10-digit phone number required.';
const zipCodeErrorMessage = 'Please enter a zip code containing 5 numbers.';
const termsErrorMessage = 'You must accept the Terms and Conditions of Service & Privacy Policy';
const addressRegularExpression = 'w*[a-zA-Z]w*';
const addressErrorMessage = 'Must include street name';
const firstDeliveryDateErrorMessage =
  'Date no longer available. Please select another delivery date.';
const passwordErrorMessage = 'Password should be minimum 8 characters long.';

// Reusable field definitions
const stringField = Yup.string().required(requiredField).nullable();
const nullableStringField = Yup.string().nullable();
const zipCodeField = Yup.string()
  .required(requiredField)
  .min(5, zipCodeErrorMessage)
  .max(5, zipCodeErrorMessage)
  .nullable();
const phoneNumberField = Yup.string()
  .required(requiredField)
  .matches(/^(1)?\d{3}-\d{3}-\d{4}$|^1?\d{10}$/, phoneNumberErrorMessage)
  .nullable();
const cityField = Yup.string().required(requiredField).max(35, 'Exceeds 35-character max');
const address2Field = Yup.string().max(64, 'Exceeds 64-character max');

// referenced in components/signUpFunnel/checkout/CheckoutForm.js
export const nullableAddressFormSchema = Yup.object().shape({
  phoneNumber: phoneNumberField,
  fullName: Yup.string().when('vendor', {
    is: isNotStripe,
    then: stringField,
    otherwise: nullableStringField,
  }),
  streetAddress: Yup.string().when('vendor', {
    is: isNotStripe,
    then: schema =>
      schema
        .required(requiredField)
        .matches(addressRegularExpression, addressErrorMessage)
        .nullable(),
    otherwise: nullableStringField,
  }),
  apartmentNumber: Yup.string().when('vendor', {
    is: isNotStripe,
    then: address2Field,
    otherwise: nullableStringField,
  }),
  city: Yup.string().when('vendor', {
    is: isNotStripe,
    then: cityField,
    otherwise: nullableStringField,
  }),
  state: Yup.string().when('vendor', {
    is: isNotStripe,
    then: stringField,
    otherwise: nullableStringField,
  }),
  zipCode: Yup.string().when('vendor', {
    is: isNotStripe,
    then: zipCodeField,
    otherwise: nullableStringField,
  }),
  smsChecked: Yup.boolean().oneOf([true, false]),
  firstDeliveryDate: Yup.string().required(firstDeliveryDateErrorMessage),
  mobileSignUp: Yup.boolean(),
  termsChecked: Yup.boolean().required(termsErrorMessage).oneOf([true], termsErrorMessage),
  password: Yup.string().when('mobileSignUp', {
    is: true,
    then: Yup.string().required(requiredField).min(8, passwordErrorMessage),
    otherwise: nullableStringField,
  }),
});

// referenced in components/account/verification/EditAddress.js
export const verifyAddressFormSchema = Yup.object().shape({
  streetAddress: Yup.string()
    .required(requiredField)
    .max(64, 'Address cannot exceed 64 characters')
    .matches(addressRegularExpression, addressErrorMessage)
    .nullable(),
  apartmentNumber: address2Field,
  city: cityField,
  state: stringField,
  zipCode: zipCodeField,
});

export const introFormSchema = Yup.object({
  introZipCode: Yup.string()
    .min(5, 'Please enter a zip code containing 5 numbers.')
    .required('Zip Code Required.'),
  introEmail: Yup.string().email('Please enter a valid email address.').required('Email Required.'),
  introSmsOptIn: Yup.boolean(),
  introSmsPhoneNumber: Yup.string().when('introSmsOptIn', {
    is: true,
    then: Yup.string()
      .required(requiredField)
      .matches(/^(1)?\d{3}-\d{3}-\d{4}$|^1?\d{10}$/, phoneNumberErrorMessage),
    otherwise: nullableStringField,
  }),
});

export const pickMealsUserFormSchema = Yup.object().shape({
  zipCode: Yup.string()
    .required('Enter zip code to continue')
    .min(5, 'Invalid zip code')
    .max(5, 'Invalid zip code')
    .nullable(),
  email: Yup.string().email('Invalid email address').required('Enter email to continue').nullable(),
});

// referenced in forms/account/AddressForm.js
export const accountSettingsAddressValidationSchema = Yup.object().shape({
  streetAddress: Yup.string()
    .required(requiredField)
    .max(64, 'Address cannot exceed 64 characters')
    .matches(addressRegularExpression, addressErrorMessage),
  address2: address2Field,
  city: cityField,
  state: Yup.string().required(requiredField),
  zip: Yup.string()
    .required(requiredField)
    .min(5, 'Zip must be 5 digits')
    .max(5, 'Zip must be 5 digits'),
});
